import { useDireccionesEntrega } from '../../hooks/useClientes'
import { usePerfilCliente } from '../../hooks/usePerfilCliente'
import type { DireccionEntregaElegida } from './filas'

/**
 * Selector de direcciones de entrega (multi-reparto), compartido por el panel
 * "Configurar" y el modal "Revisar y publicar".
 *
 * Muestra SIEMPRE las dos: la dirección PRINCIPAL del cliente (la de su ficha,
 * marcada por defecto) y las ADICIONALES guardadas. Cada marcada genera un
 * albarán (misma facturación).
 *
 * Convención de `elegidas` (lo que viaja al publicar):
 *  - Lista vacía []  → va SOLO a la principal (comportamiento por defecto).
 *  - Contiene la principal (id === PRINCIPAL_ID) → se emite a la ficha.
 *  - Contiene adicionales (id real) → un albarán a cada una.
 * La principal se representa con id 0 y SIN `direccion_entrega` al publicar, así
 * el backend usa la dirección de la ficha. aEntradasPublicar la trata aparte.
 */
export const PRINCIPAL_ID = 0

export function SelectorMultiReparto({
  contactId,
  elegidas,
  onCambiar,
  compacto = false,
  titulo,
}: {
  contactId: string
  elegidas: DireccionEntregaElegida[]
  onCambiar?: (dirs: DireccionEntregaElegida[]) => void
  /** compacto = estilo para dentro del modal (sin el marco de Grupo). */
  compacto?: boolean
  /** Nombre del cliente a mostrar como cabecera (solo si hay direcciones). */
  titulo?: string
}) {
  const { data: perfil } = usePerfilCliente(contactId)
  const { data } = useDireccionesEntrega(contactId)
  const adicionales = Array.isArray(data) ? data : []

  if (!onCambiar) return null
  // Si el cliente no tiene direcciones adicionales, no hay nada que elegir.
  if (adicionales.length === 0) return null

  const principal: DireccionEntregaElegida = {
    id: PRINCIPAL_ID,
    referencia: 'Dirección principal',
    destinatario: null,
    direccion: perfil?.direccion ?? '',
    codigoPostal: perfil?.codigoPostal ?? null,
    ciudad: perfil?.poblacion ?? null,
    provincia: perfil?.provincia ?? null,
  }
  const opciones: DireccionEntregaElegida[] = [
    principal,
    ...adicionales.map((d) => ({
      id: d.id,
      referencia: d.referencia,
      destinatario: d.destinatario,
      direccion: d.direccion,
      codigoPostal: d.codigoPostal,
      ciudad: d.ciudad,
      provincia: d.provincia,
    })),
  ]

  // La principal está marcada por defecto: lista vacía = solo principal.
  const estaMarcada = (id: number) =>
    id === PRINCIPAL_ID
      ? elegidas.length === 0 || elegidas.some((d) => d.id === PRINCIPAL_ID)
      : elegidas.some((d) => d.id === id)

  const alternar = (op: DireccionEntregaElegida) => {
    // Estado efectivo actual: si elegidas está vacío, equivale a [principal].
    const actual = elegidas.length === 0 ? [principal] : elegidas
    const yaEsta = actual.some((d) => d.id === op.id)
    let siguiente = yaEsta ? actual.filter((d) => d.id !== op.id) : [...actual, op]
    // Nunca dejar nada marcado: si se queda vacío, vuelve a la principal.
    if (siguiente.length === 0) siguiente = [principal]
    // Si queda SOLO la principal, se normaliza a [] (comportamiento por defecto).
    if (siguiente.length === 1 && siguiente[0].id === PRINCIPAL_ID) siguiente = []
    onCambiar(siguiente)
  }

  const nAlbaranes = elegidas.length === 0 ? 1 : elegidas.length

  return (
    <div className={compacto ? 'rounded-lg border border-acento-borde bg-acento-tinta-suave/40 px-3 py-2.5' : 'flex flex-col gap-2'}>
      {compacto && titulo ? (
        <p className="mb-1 text-sm font-semibold text-tinta">Direcciones de entrega — {titulo}</p>
      ) : null}
      {!compacto ? (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
            Direcciones de entrega (multi-reparto)
          </span>
          <span className="h-px flex-1 bg-borde" />
        </div>
      ) : null}
      <p className="text-xs text-tinta-apagada">
        Marca a qué direcciones toca reparto. Cada una genera su albarán con la misma facturación.
        La principal viene marcada por defecto.
      </p>
      <div className="flex flex-col gap-1.5">
        {opciones.map((op) => (
          <label
            key={op.id}
            className="flex cursor-pointer items-start gap-2 rounded-lg border border-borde bg-superficie px-3 py-2 hover:bg-elevado"
          >
            <input
              type="checkbox"
              checked={estaMarcada(op.id)}
              onChange={() => alternar(op)}
              className="mt-0.5"
              aria-label={op.referencia || op.direccion}
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-tinta">
                  {op.referencia || op.direccion || 'Dirección principal'}
                </span>
                {op.id === PRINCIPAL_ID ? (
                  <span className="shrink-0 rounded-full bg-positivo-fondo px-2 py-0.5 text-[10px] font-semibold text-positivo-tinta">
                    Principal
                  </span>
                ) : null}
              </span>
              <span className="block truncate text-xs text-tinta-apagada">
                {[op.direccion, op.codigoPostal, op.ciudad].filter(Boolean).join(', ') || 'Dirección de la ficha'}
              </span>
            </span>
          </label>
        ))}
      </div>
      <p className="text-xs font-medium text-acento-tinta">
        {nAlbaranes} {nAlbaranes === 1 ? 'albarán' : 'albaranes'} se {nAlbaranes === 1 ? 'generará' : 'generarán'}
      </p>
    </div>
  )
}
